import { useState, useEffect } from "react";

import { cn } from "@/lib/utils";

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

interface CountdownTimerProps {
  /** Target date as ISO string */
  targetDate: string;
  /** Title of the countdown */
  title: string;
  /** Optional description */
  description?: string;
  /** Theme variant */
  theme?: "default" | "urgent" | "celebration" | "dark";
  /** Show seconds in countdown */
  showSeconds?: boolean;
  /** Show description */
  showDescription?: boolean;
  /** Custom colors */
  customColors?: {
    background?: string;
    text?: string;
    accent?: string;
  };
  /** Is this a widget embed (simplified layout) */
  isWidget?: boolean;
  /** Callback when countdown reaches zero */
  onComplete?: () => void;
  /** Custom CSS class */
  className?: string;
}

function calculateTimeRemaining(targetDate: string): TimeRemaining {
  const target = new Date(targetDate).getTime();
  const now = new Date().getTime();
  const total = target - now;

  if (total <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }

  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hours = Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((total % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, total };
}

export default function CountdownTimer({
  targetDate,
  title,
  description,
  theme = "default",
  showSeconds = true,
  showDescription = true,
  customColors,
  isWidget = false,
  onComplete,
  className,
}: CountdownTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(
    calculateTimeRemaining(targetDate),
  );
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTime = calculateTimeRemaining(targetDate);
      setTimeRemaining(newTime);

      if (newTime.total <= 0 && !isCompleted) {
        setIsCompleted(true);
        onComplete?.();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, isCompleted, onComplete]);

  // Theme styles
  const themeStyles = {
    default: {
      background: "bg-gradient-to-br from-blue-500 via-purple-600 to-blue-700",
      text: "text-white",
      accent: "text-blue-200",
      card: "bg-white/10 backdrop-blur-md border border-white/20",
      glow: "shadow-blue-500/25",
    },
    urgent: {
      background: "bg-gradient-to-br from-red-500 via-orange-600 to-red-700",
      text: "text-white",
      accent: "text-red-200",
      card: "bg-white/10 backdrop-blur-md border border-white/20",
      glow: "shadow-red-500/25",
    },
    celebration: {
      background:
        "bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600",
      text: "text-white",
      accent: "text-yellow-200",
      card: "bg-white/10 backdrop-blur-md border border-white/20",
      glow: "shadow-pink-500/25",
    },
    dark: {
      background: "bg-gradient-to-br from-gray-900 via-gray-800 to-black",
      text: "text-white",
      accent: "text-gray-300",
      card: "bg-white/5 backdrop-blur-md border border-white/10",
      glow: "shadow-gray-500/25",
    },
  };

  const currentTheme = themeStyles[theme];

  // Custom color overrides
  const styleOverrides = customColors
    ? {
        ...(customColors.background && {
          backgroundColor: customColors.background,
        }),
        ...(customColors.text && { color: customColors.text }),
      }
    : {};

  if (isCompleted) {
    return (
      <div
        className={cn(
          "relative overflow-hidden rounded-2xl p-8 text-center",
          currentTheme.background,
          currentTheme.text,
          "shadow-2xl",
          currentTheme.glow,
          className,
        )}
        style={styleOverrides}
      >
        <div className="relative z-10">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-bold mb-2">{title}</h2>
          <p className="text-lg opacity-90">Event has started!</p>
        </div>

        {/* Celebration animation background */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />
      </div>
    );
  }

  const timeUnits = [
    { label: "Days", value: timeRemaining.days, show: true },
    { label: "Hours", value: timeRemaining.hours, show: true },
    { label: "Minutes", value: timeRemaining.minutes, show: true },
    { label: "Seconds", value: timeRemaining.seconds, show: showSeconds },
  ].filter((unit) => unit.show);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl p-8",
        currentTheme.background,
        currentTheme.text,
        "shadow-2xl",
        currentTheme.glow,
        !isWidget && "min-h-[400px]",
        className,
      )}
      style={styleOverrides}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent" />
        <div
          className="absolute inset-0 bg-repeat opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
            backgroundSize: "20px 20px",
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Title */}
        <div className="text-center mb-8">
          <h1
            className={cn(
              "text-4xl font-bold mb-2",
              isWidget ? "text-2xl" : "text-4xl md:text-5xl",
            )}
          >
            {title}
          </h1>
          {description && showDescription && (
            <p
              className={cn(
                "opacity-90",
                isWidget ? "text-sm" : "text-lg md:text-xl",
              )}
            >
              {description}
            </p>
          )}
        </div>

        {/* Countdown Display */}
        <div
          className={cn(
            "grid gap-4 mb-6",
            timeUnits.length === 4
              ? "grid-cols-2 md:grid-cols-4"
              : "grid-cols-3",
            isWidget && "gap-2",
          )}
        >
          {timeUnits.map((unit) => (
            <div
              key={unit.label}
              className={cn(
                "text-center p-4 rounded-xl",
                currentTheme.card,
                "backdrop-blur-md",
                "transform transition-all duration-300 hover:scale-105",
                isWidget && "p-2",
              )}
            >
              <div
                className={cn(
                  "text-3xl font-bold tabular-nums",
                  isWidget ? "text-xl" : "text-3xl md:text-4xl lg:text-5xl",
                  "bg-gradient-to-b from-current to-current/80 bg-clip-text",
                )}
              >
                {unit.value.toString().padStart(2, "0")}
              </div>
              <div
                className={cn(
                  "text-xs font-medium uppercase tracking-wide mt-1",
                  currentTheme.accent,
                  isWidget ? "text-xs" : "text-sm",
                )}
              >
                {unit.label}
              </div>
            </div>
          ))}
        </div>

        {/* Progress Indicator */}
        {!isWidget && (
          <div className="relative">
            <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-white/60 to-white/40 rounded-full transition-all duration-1000 ease-out"
                style={{
                  width: `${Math.max(
                    0,
                    Math.min(
                      100,
                      (1 - timeRemaining.total / (30 * 24 * 60 * 60 * 1000)) *
                        100,
                    ),
                  )}%`,
                }}
              />
            </div>
            <div className="text-center mt-2">
              <span className={cn("text-xs", currentTheme.accent)}>
                Time Remaining
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Animated background elements */}
      <div className="absolute top-4 right-4 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse" />
      <div
        className="absolute bottom-4 left-4 w-24 h-24 bg-white/5 rounded-full blur-xl animate-pulse"
        style={{ animationDelay: "2s" }}
      />
    </div>
  );
}
